/**
 * JsonValueInput — an input for a JSON-typed value (a state variable's `initial`,
 * $page or $global). Shared by `StateEditor` + `GlobalStateEditor`.
 *
 * WHY this exists: a naive `value={JSON.stringify(value)}` + `onChange` that
 * `JSON.parse`s every keystroke is UN-EDITABLE — mid-typing the text isn't valid
 * JSON (`"`, `"hel`, …), so the parse throws, the model isn't updated, and the
 * controlled input snaps back to the old stringified value. You can never build
 * up a value. (This bit both editors.) Instead we keep the user's RAW text in
 * local draft state and only push a parsed value to the model when it's valid;
 * the displayed value is the draft, so typing always works. On blur we
 * re-normalise the text from the model (canonical JSON). While focused we never
 * clobber the draft from prop changes.
 */

import { useEffect, useState } from "react";

/** Canonical text for a JSON value — but null/undefined render as an EMPTY input
 *  (the placeholder shows instead of a literal "null", which reads as noise).
 *  A real `0` / `false` / `""` still shows. */
function toText(value: unknown): string {
  return value === null || value === undefined ? "" : JSON.stringify(value);
}

export function JsonValueInput({
  value,
  onChange,
  className,
  placeholder,
  "aria-label": ariaLabel,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  className?: string;
  placeholder?: string;
  "aria-label"?: string;
}) {
  const [text, setText] = useState(() => toText(value));
  const [focused, setFocused] = useState(false);

  // Mirror the external value into the draft ONLY while not editing, so a
  // re-seed / sibling update shows, but in-progress typing is never clobbered.
  useEffect(() => {
    if (!focused) setText(toText(value));
  }, [value, focused]);

  return (
    <input
      aria-label={ariaLabel}
      className={className}
      placeholder={placeholder}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw); // always reflect what the user typed
        try {
          onChange(JSON.parse(raw || "null"));
        } catch {
          /* not parseable yet — keep the draft, leave the model unchanged */
        }
      }}
      onBlur={() => {
        setFocused(false);
        // Commit + normalise: an empty / unparseable draft falls back to null.
        try {
          onChange(JSON.parse(text || "null"));
        } catch {
          onChange(null);
        }
      }}
    />
  );
}
